import { useEffect, useRef, useCallback } from 'react';
import { Stack, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { palette } from '../../theme';
import { dictionary } from '../../dictionary';
import TeamMemberDrawerHero from './TeamMemberDrawerHero';
import TeamMemberDrawerContent from './TeamMemberDrawerContent';
import type { TeamMemberDrawerProps } from './TeamMemberDrawer.types';

export default function TeamMemberDrawer({ member, phase, onClose }: TeamMemberDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  // Focus management
  useEffect(() => {
    if (phase === 'open') {
      previousFocus.current = document.activeElement as HTMLElement;
      drawerRef.current?.focus();
    }
    if (phase === 'closed' && previousFocus.current) {
      previousFocus.current.focus();
      previousFocus.current = null;
    }
  }, [phase]);

  // Escape key
  useEffect(() => {
    if (phase !== 'open') return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [phase, onClose]);

  // Tab trap
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !drawerRef.current) return;
    const focusable = drawerRef.current.querySelectorAll<HTMLElement>(
      'a[href], button, [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  if (!member && phase === 'closed') return null;

  const isVisible = phase === 'open';

  return (
    <Stack
      ref={drawerRef}
      role='dialog'
      aria-modal='true'
      aria-label={member?.name}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
        transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        outline: 'none',
        overflowY: 'auto',
        backgroundColor: palette.backgroundPrimary,
      }}
    >
      {member && (
        <>
          <IconButton
            onClick={onClose}
            aria-label={dictionary.about.team.drawer.close}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10,
              width: 48,
              height: 48,
              border: `1px solid ${palette.border}`,
              color: palette.white,
              '& .MuiSvgIcon-root': {
                transition: 'transform 0.2s ease',
              },
              '&:hover .MuiSvgIcon-root': {
                transform: 'scale(1.25)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <TeamMemberDrawerHero member={member} />
          <TeamMemberDrawerContent member={member} onClose={onClose} />
        </>
      )}
    </Stack>
  );
}
